import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getMyJobsAction } from "../../../redux/actions/jobActions";
import { SectionLoader } from "../../../components/PageLoader";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import WorkOutlineIcon from "@material-ui/icons/WorkOutline";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import AddIcon from "@material-ui/icons/Add";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { IconButton, InputAdornment, TextField } from "@material-ui/core";
import SlidingPane from "react-sliding-pane";
import "./JobTemplateSelector.css";

const JobTemplateSelector = ({ open, onClose, onSelectTemplate, onCreateNew }) => {
  const dispatch = useDispatch();
  const [mode, setMode] = useState("template"); // "template" or "new"
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  const { myJobs } = useSelector(({ job }) => job);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (open) {
      setLoading(true);
      Promise.resolve(getMyJobsAction(dispatch)).finally(() =>
        setLoading(false)
      );
      // Reset state when modal opens
      setMode("template");
      setSearchTerm("");
      setSelectedJob(null);
    }
  }, [open, dispatch]);

  const filteredJobs = (myJobs || []).filter((job) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const title = (job.role_title || "").toLowerCase();
    const company = (job.campany_name || job.company_name || "").toLowerCase();
    const location = (job.location || "").toLowerCase();
    return (
      title.includes(term) ||
      company.includes(term) ||
      location.includes(term)
    );
  });

  const handleContinue = () => {
    if (mode === "new") {
      onCreateNew();
    } else if (mode === "template" && selectedJob) {
      onSelectTemplate(selectedJob);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getStatusLabel = (job) => {
    if (job.is_draft === 1 || job.is_draft === "1") return "Draft";
    if (job.isSuspended === "1") return "On Hold";
    return "Published";
  };

  const getStatusClass = (job) => {
    if (job.is_draft === 1 || job.is_draft === "1") return "status-draft";
    if (job.isSuspended === "1") return "status-hold";
    return "status-published";
  };

  return (
    <SlidingPane
      isOpen={open}
      title="Create a Job Post"
      from="right"
      width={viewportWidth <= 768 ? "100%" : "80%"}
      onRequestClose={onClose}
      overlayClassName="jts-pane-overlay"
    >
      <div className="jts-container">
        {/* Body */}
        <div className="jts-body">
          {/* Question */}
          <p className="jts-question">
            How would you like to post your job? <span className="jts-required">*</span>
          </p>

          {/* Radio Options */}
          <div className="jts-options">
            <label
              className={`jts-option ${mode === "template" ? "jts-option-active" : ""}`}
              onClick={() => setMode("template")}
            >
                 <FileCopyOutlinedIcon className="jts-option-icon" />

              <span className="jts-option-text">Use a previous job as a template</span>
              <span className="jts-save-badge">Save time</span>
            </label>

            <label
              className={`jts-option ${mode === "new" ? "jts-option-active" : ""}`}
              onClick={onCreateNew}
            >
              <AddIcon className="jts-option-icon" />
              <span className="jts-option-text">Create a brand new post</span>
            </label>
          </div>

          {/* Template Selection Panel */}
          {mode === "template" && (
            <div className="jts-template-panel">
              {/* Search */}
              <TextField
                className="jts-search"
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Search by job title"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm("")}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              {/* Results count */}
              <div className="jts-results-info">
                <WorkOutlineIcon style={{ fontSize: 18, color: "#6b7280" }} />
                <span>{filteredJobs.length} results</span>
              </div>

              {/* Job List */}
              <div className="jts-job-list">
                {loading ? (
                  <div className="jts-loading">
                    <SectionLoader />
                  </div>
                ) : filteredJobs.length === 0 ? (
                  <div className="jts-empty">
                    <WorkOutlineIcon style={{ fontSize: 48, color: "#d1d5db" }} />
                    <p>No jobs found</p>
                  </div>
                ) : (
                  filteredJobs.map((job) => (
                    <div
                      key={job.id}
                      className={`jts-job-item ${selectedJob?.id === job.id ? "jts-job-selected" : ""}`}
                      onClick={() => setSelectedJob(job)}
                    >
                      <div className="jts-job-info">
                        <div className="jts-job-title">{job.role_title || "Untitled"}</div>
                        <div className="jts-job-meta">
                          {job.campany_name || job.company_name || ""}
                          {job.location ? ` · ${job.location}` : ""}
                          {job.created_at ? ` · ${formatDate(job.created_at)}` : ""}
                        </div>
                      </div>
                      <div className="jts-job-right">
                        <span className={`jts-status ${getStatusClass(job)}`}>
                          {getStatusLabel(job)}
                        </span>
                        {selectedJob?.id === job.id && (
                          <CheckCircleIcon className="jts-check-icon" />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* New post message */}
          {mode === "new" && (
            <div className="jts-new-panel">
              {/* <AddIcon style={{ fontSize: 48, color: "#3a6ed4" }} /> */}
              <h3>Start from scratch</h3>
              <p>You'll be taken to a blank job posting form where you can fill in all the details.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="jts-footer">
          <button className="jts-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="jts-btn-continue"
            disabled={mode === "template" && !selectedJob}
            onClick={handleContinue}
          >
            Continue
          </button>
        </div>
      </div>
    </SlidingPane>
  );
};

export default JobTemplateSelector;
